import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Input,
  Tag,
  Typography,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  AudioOutlined,
  MessageOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  StopOutlined
} from '@ant-design/icons';
import styles from './VoiceModeView.module.css';

const { Text, Paragraph, Title } = Typography;
const { TextArea } = Input;

/**
 * VoiceModeView - Voice and Assistant input mode interface
 *
 * Extracted from ReportWriterV2 to improve modularity.
 * Handles both voice recording and assistant text input modes.
 */
const VoiceModeView = ({
  selectedReport,
  voiceSections,
  currentSection,
  sectionTranscripts,
  formData,
  inputMode,
  voiceTranscript,
  isRecording,
  loading,
  onUpdateFormData,
  onSetCurrentSection,
  onStartVoiceInput,
  onHandleVoiceToAI,
  onSetSectionTranscripts,
  onSetVoiceTranscript,
  onSetInputMode,
  onPreviousSection,
  onNextSection
}) => {
  const currentSectionData = voiceSections.find(s => s.key === currentSection);
  const completedSections = voiceSections.filter(s => sectionTranscripts[s.key]?.formatted).length;
  const currentIndex = voiceSections.findIndex(s => s.key === currentSection);
  const isFirstSection = currentIndex === 0;
  const isLastSection = currentIndex === voiceSections.length - 1;

  // Helper function to calculate character count and determine if over limit
  const getCharCount = (sectionKey) => {
    const formatted = sectionTranscripts[sectionKey]?.formatted || '';
    return formatted.length;
  };

  const getCharLimitDisplay = (section) => {
    if (!section.charLimit) return null;

    const currentCount = getCharCount(section.key);
    const isOverLimit = currentCount > section.charLimit;

    return (
      <span className={isOverLimit ? styles.charCounterOverLimit : styles.charCounter}>
        ({currentCount}/{section.charLimit})
      </span>
    );
  };

  return (
    <div className={styles.container}>
      {/* Main workspace */}
      <Row gutter={16} className={styles.mainWorkspace}>
        {/* Left Column: Title field + Vertical section list */}
        <Col span={6} className={styles.leftColumn}>
          {/* Title Field */}
          <Card size="small" className={styles.titleCard}>
            <div className={styles.titleLabel}>
              <Text strong>
                {selectedReport === 'knowledge_gap' ? 'Knowledge Gap Title' : 'Key Decision Title'}
              </Text>
            </div>
            <Input.TextArea
              value={formData.title || ''}
              onChange={(e) => onUpdateFormData({ title: e.target.value })}
              placeholder="Enter report title"
              autoSize={{ minRows: 2, maxRows: 6 }}
              className={styles.titleTextarea}
            />
          </Card>

          {/* Sections List */}
          <Card title="Sections" size="small" className={styles.sectionsCard}>
            <div className={styles.completedTag}>
              <Tag color="blue">{completedSections}/{voiceSections.length} completed</Tag>
            </div>
            <Space direction="vertical" size="small" className={styles.sectionsList}>
              {voiceSections.map((section) => {
                const transcript = sectionTranscripts[section.key];
                const isActive = section.key === currentSection;

                return (
                  <Card
                    key={section.key}
                    size="small"
                    hoverable={!isActive}
                    onClick={() => onSetCurrentSection(section.key)}
                    className={isActive ? styles.sectionCardActive : (transcript?.formatted ? styles.sectionCardCompleted : styles.sectionCard)}
                  >
                    <Row justify="space-between" align="middle">
                      <Col flex="auto">
                        <Text strong={isActive}>
                          {section.number}. {section.label}
                        </Text>
                      </Col>
                      <Col>
                        {transcript?.loading && <Tag color="processing">...</Tag>}
                        {transcript?.formatted && <CheckCircleOutlined className={styles.sectionCheckIcon} />}
                      </Col>
                    </Row>
                  </Card>
                );
              })}
            </Space>
          </Card>
        </Col>

        {/* Right Column: Active section with transcript and support text side-by-side */}
        <Col span={18} className={styles.rightColumn}>
          {currentSectionData && (
            <Row gutter={16} className={styles.contentRow}>
              {/* Left: Transcript and voice input */}
              <Col span={12} className={styles.transcriptColumn}>
                <Card
                  title={
                    <Space>
                      {inputMode === 'voice' && <AudioOutlined className={isRecording ? styles.voiceIconRecording : styles.voiceIconReady} />}
                      {inputMode === 'assistant' && <MessageOutlined className={styles.assistantIcon} />}
                      <Text strong>
                        {currentSectionData?.number}. {currentSectionData?.label}
                        {currentSectionData && getCharLimitDisplay(currentSectionData)}
                      </Text>
                    </Space>
                  }
                  extra={
                    <Space>
                      <Button
                        size="small"
                        icon={<ArrowLeftOutlined />}
                        onClick={onPreviousSection}
                        disabled={isFirstSection}
                      >
                        Previous
                      </Button>
                      <Button
                        size="small"
                        icon={<ArrowRightOutlined />}
                        onClick={onNextSection}
                        disabled={isLastSection}
                      >
                        Next
                      </Button>
                    </Space>
                  }
                  className={styles.transcriptCard}
                  styles={{ body: { flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' } }}
                >
                  <Space direction="vertical" size="middle" className={styles.contentSpace}>
                    {/* Voice mode: Prominent recording CTA */}
                    {inputMode === 'voice' && !sectionTranscripts[currentSection]?.formatted && (!voiceTranscript || isRecording) && (
                      <div className={isRecording ? styles.recordingCtaActive : styles.recordingCta}>
                        <AudioOutlined className={isRecording ? styles.audioIconRecording : styles.audioIcon} />
                        <Title level={3} className={styles.recordingTitle}>
                          {isRecording ? 'Listening...' : 'Ready to Record'}
                        </Title>
                        <Paragraph type="secondary" className={styles.recordingDescription}>
                          {isRecording
                            ? 'Speak naturally. AI will format your words into professional text.'
                            : 'Click the button below and speak your thoughts about this section.'}
                        </Paragraph>
                        <Button
                          type="primary"
                          size="large"
                          icon={isRecording ? <StopOutlined /> : <AudioOutlined />}
                          onClick={onStartVoiceInput}
                          loading={loading}
                          danger={isRecording}
                          className={styles.recordingButton}
                        >
                          {isRecording ? 'Stop Recording' : 'Start Recording'}
                        </Button>
                        {isRecording && (
                          <div className={styles.recTagContainer}>
                            <Tag color="red" className={styles.recTag}>
                              <span>● REC</span>
                            </Tag>
                          </div>
                        )}
                      </div>
                    )}

                    {/* For assistant mode: text input area */}
                    {inputMode === 'assistant' && !sectionTranscripts[currentSection]?.formatted && (
                      <div>
                        <Text type="secondary" strong>Type your casual notes:</Text>
                        <TextArea
                          rows={6}
                          placeholder="Type casually here... AI will format it professionally"
                          className={styles.assistantTextarea}
                          value={voiceTranscript}
                          onChange={(e) => onSetVoiceTranscript(e.target.value)}
                        />
                        <Button
                          type="primary"
                          className={styles.formatButton}
                          onClick={() => {
                            if (voiceTranscript.trim()) {
                              onHandleVoiceToAI(voiceTranscript);
                            }
                          }}
                          loading={loading}
                          disabled={!voiceTranscript.trim()}
                        >
                          Format with AI
                        </Button>
                      </div>
                    )}

                    {/* Show raw transcript (if recording or just recorded - voice mode only) */}
                    {inputMode === 'voice' && voiceTranscript && (
                      <div>
                        <Text type="secondary" strong>Raw Transcript:</Text>
                        <Paragraph className={styles.rawTranscript}>
                          {voiceTranscript}
                        </Paragraph>
                      </div>
                    )}

                    {/* Show saved raw transcript above formatted */}
                    {sectionTranscripts[currentSection]?.raw && !voiceTranscript && (
                      <div>
                        <Text type="secondary" strong>{inputMode === 'voice' ? 'Raw Transcript:' : 'Your Input:'}</Text>
                        <Paragraph className={styles.rawTranscript}>
                          {sectionTranscripts[currentSection].raw}
                        </Paragraph>
                      </div>
                    )}

                    {/* Show formatted content below raw */}
                    {sectionTranscripts[currentSection]?.formatted && (
                      <div>
                        <Text type="secondary" strong>Formatted Content:</Text>
                        <Paragraph className={styles.formattedContent}>
                          {sectionTranscripts[currentSection].formatted}
                        </Paragraph>
                        <Space>
                          <Button
                            size="small"
                            onClick={() => {
                              // Clear formatted content to allow re-entry
                              onSetSectionTranscripts(prev => ({
                                ...prev,
                                [currentSection]: {
                                  ...prev[currentSection],
                                  formatted: null
                                }
                              }));
                            }}
                          >
                            {inputMode === 'voice' ? 'Re-record' : 'Edit Input'}
                          </Button>
                          <Button
                            size="small"
                            type="primary"
                            onClick={() => onSetInputMode('direct')}
                          >
                            Edit in Direct Mode
                          </Button>
                        </Space>
                      </div>
                    )}

                    {sectionTranscripts[currentSection]?.loading && (
                      <Alert
                        message={inputMode === 'voice' ? 'Processing your voice input...' : 'Processing your text input...'}
                        type="info"
                        showIcon
                      />
                    )}
                  </Space>
                </Card>
              </Col>

              {/* Right: Support text */}
              <Col span={12} className={styles.supportColumn}>
                <Card
                  title={<Text strong className={styles.supportTitle}>💡 What to think about</Text>}
                  className={styles.supportCard}
                  styles={{ body: { flex: 1, overflowY: 'auto', padding: '20px' } }}
                >
                  {currentSectionData?.supportText ? (
                    <div className={styles.supportText}>
                      {currentSectionData.supportText}
                    </div>
                  ) : (
                    <div className={styles.noGuidance}>
                      <Text type="secondary">No guidance available for this section.</Text>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

VoiceModeView.propTypes = {
  selectedReport: PropTypes.oneOf(['knowledge_gap', 'key_decision']).isRequired,
  voiceSections: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      number: PropTypes.number.isRequired,
      label: PropTypes.string.isRequired,
      supportText: PropTypes.string
    })
  ).isRequired,
  currentSection: PropTypes.string.isRequired,
  sectionTranscripts: PropTypes.objectOf(
    PropTypes.shape({
      raw: PropTypes.string,
      formatted: PropTypes.string,
      loading: PropTypes.bool
    })
  ).isRequired,
  formData: PropTypes.shape({
    title: PropTypes.string
  }).isRequired,
  inputMode: PropTypes.oneOf(['voice', 'assistant']).isRequired,
  voiceTranscript: PropTypes.string.isRequired,
  isRecording: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onUpdateFormData: PropTypes.func.isRequired,
  onSetCurrentSection: PropTypes.func.isRequired,
  onStartVoiceInput: PropTypes.func.isRequired,
  onHandleVoiceToAI: PropTypes.func.isRequired,
  onSetSectionTranscripts: PropTypes.func.isRequired,
  onSetVoiceTranscript: PropTypes.func.isRequired,
  onSetInputMode: PropTypes.func.isRequired,
  onPreviousSection: PropTypes.func.isRequired,
  onNextSection: PropTypes.func.isRequired
};

export default VoiceModeView;
